import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Plus, Pencil, Trash2, Package, ImageOff, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem("session_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CURRENCIES = ["MXN", "USD", "EUR", "GBP", "CAD", "BRL", "COP", "ARS", "CLP"];

const emptyForm = () => ({
  name: "",
  description: "",
  price: "",
  currency: "MXN",
  image_url: "",
  active: true,
});

export default function CatalogManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/products`, {
        headers: getAuthHeaders(),
        credentials: "include",
      });
      if (res.ok) setProducts(await res.json());
    } catch (e) {
      toast.error("Error al cargar el catálogo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowDialog(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      description: p.description || "",
      price: String(p.price ?? ""),
      currency: p.currency || "MXN",
      image_url: p.image_url || "",
      active: p.active !== false,
    });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Ponle un nombre al producto");
    const priceNum = parseFloat(form.price);
    if (isNaN(priceNum) || priceNum < 0) return toast.error("Precio inválido");
    setSaving(true);
    try {
      const url = editing
        ? `${API}/api/products/${editing.product_id}`
        : `${API}/api/products`;
      const method = editing ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify({
          name: form.name.trim(),
          description: form.description.trim(),
          price: priceNum,
          currency: form.currency,
          image_url: form.image_url.trim() || null,
          active: form.active,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Error al guardar");
      }
      toast.success(editing ? "Producto actualizado" : "Producto creado");
      setShowDialog(false);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"?`)) return;
    try {
      const res = await fetch(`${API}/api/products/${p.product_id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Error al eliminar");
      toast.success("Producto eliminado");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Catálogo de productos</h2>
          <p className="text-sm text-slate-400">
            Tus productos pueden enviarse a cualquier chat. Los compradores pagan con Stripe sin salir de OkChat.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Plus className="w-4 h-4 mr-2" /> Nuevo producto
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-700 rounded-lg">
          <Package className="w-10 h-10 text-slate-500 mx-auto mb-2" />
          <p className="text-slate-400">Aún no tienes productos. Crea el primero.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p) => (
            <div key={p.product_id} className="bg-slate-800/60 border border-slate-700 rounded-xl overflow-hidden">
              <div className="aspect-video bg-slate-900 flex items-center justify-center">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <ImageOff className="w-10 h-10 text-slate-600" />
                )}
              </div>
              <div className="p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-white font-medium leading-tight">{p.name}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${p.active ? "bg-emerald-600/30 text-emerald-300" : "bg-slate-600/40 text-slate-400"}`}>
                    {p.active ? "Activo" : "Inactivo"}
                  </span>
                </div>
                {p.description && <p className="text-xs text-slate-400 line-clamp-2">{p.description}</p>}
                <p className="text-emerald-400 font-semibold">
                  {p.currency} {Number(p.price).toFixed(2)}
                </p>
                <div className="flex gap-2 pt-1">
                  <Button size="sm" variant="ghost" className="flex-1 text-white hover:bg-slate-700" onClick={() => openEdit(p)}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10" onClick={() => remove(p)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar producto" : "Nuevo producto"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Nombre"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white"
            />
            <Textarea
              placeholder="Descripción (opcional)"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white"
              rows={3}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="Precio"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white"
              />
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 text-white">
                  {CURRENCIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <Input
              placeholder="URL de imagen (opcional)"
              value={form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white"
            />
            <div className="flex items-center justify-between bg-slate-800 px-3 py-2 rounded-md">
              <span className="text-sm">Visible en mi catálogo</span>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)} className="text-slate-300">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
